import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const storageKey = (userId: string) => `nazandoo_trusted_device_${userId}`;

const getDeviceName = (): string => {
  const ua = navigator.userAgent;
  const browser =
    ua.includes('Edg/')    ? 'Edge' :
    ua.includes('Chrome')  ? 'Chrome' :
    ua.includes('Firefox') ? 'Firefox' :
    ua.includes('Safari')  ? 'Safari' : 'Navigateur';

  if (/iPhone/.test(ua))  return `iPhone - ${browser}`;
  if (/iPad/.test(ua))    return `iPad - ${browser}`;
  if (/Android/.test(ua)) return `Android - ${browser}`;
  if (/Windows/.test(ua)) return `Windows - ${browser}`;
  if (/Mac/.test(ua))     return `Mac - ${browser}`;
  if (/Linux/.test(ua))   return `Linux - ${browser}`;
  return `${browser}`;
};

export interface TrustedDevice {
  id: string;
  device_name: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  expires_at: string;
  last_used_at: string;
  revoked_at: string | null;
}

export const useTrustedDevice = () => {
  /** Vérifie si l'appareil courant est de confiance pour cet userId */
  const checkTrusted = useCallback(async (userId: string): Promise<boolean> => {
    const token = localStorage.getItem(storageKey(userId));
    if (!token) return false;

    const { data } = await supabase
      .from('mfa_trusted_devices')
      .select('id')
      .eq('device_token', token)
      .eq('user_id', userId)
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .maybeSingle();

    if (!data) {
      localStorage.removeItem(storageKey(userId)); // Token invalide → nettoyer
      return false;
    }

    // Mettre à jour last_used_at
    await supabase
      .from('mfa_trusted_devices')
      .update({ last_used_at: new Date().toISOString() })
      .eq('id', data.id);

    return true;
  }, []);

  /** Enregistre cet appareil comme de confiance pour 60 jours */
  const setTrusted = useCallback(async (userId: string): Promise<void> => {
    // Récupérer la durée configurée (par défaut 60 jours)
    const { data: setting } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', 'mfa_trusted_device_days')
      .maybeSingle();

    const days = Number((setting as any)?.value ?? 60);
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + days);

    const { data } = await supabase
      .from('mfa_trusted_devices')
      .insert({
        user_id: userId,
        device_name: getDeviceName(),
        expires_at: expiresAt.toISOString(),
        user_agent: navigator.userAgent,
      })
      .select('device_token')
      .single();

    if ((data as any)?.device_token) {
      localStorage.setItem(storageKey(userId), (data as any).device_token);
    }
  }, []);

  /** Supprime le token de confiance de cet appareil */
  const clearTrusted = useCallback((userId: string): void => {
    localStorage.removeItem(storageKey(userId));
  }, []);

  /** Liste les appareils de confiance actifs de l'utilisateur connecté */
  const listMyDevices = useCallback(async (): Promise<TrustedDevice[]> => {
    const { data } = await supabase
      .from('mfa_trusted_devices')
      .select('id, device_name, user_agent, ip_address, created_at, expires_at, last_used_at, revoked_at')
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('last_used_at', { ascending: false });
    return (data as TrustedDevice[]) || [];
  }, []);

  /** Révoque un appareil de confiance de l'utilisateur connecté */
  const revokeMyDevice = useCallback(async (deviceId: string): Promise<void> => {
    await supabase
      .from('mfa_trusted_devices')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', deviceId);
  }, []);

  return { checkTrusted, setTrusted, clearTrusted, listMyDevices, revokeMyDevice };
};
